import {Box, Grid} from '@mui/material';
import Typography from '@mui/material/Typography';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import React, {useContext, useEffect, useMemo} from 'react';
import {uniqBy} from 'lodash-es';
import {LocalizationProvider} from '@mui/x-date-pickers-pro';
import {AdapterMoment} from '@mui/x-date-pickers-pro/AdapterMoment';
import {useSearchParams} from 'react-router-dom';
import {useBuilderContext} from './useBuilderContext';
import OWNERSHIP from '../../../constants/Ownership';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {AccountContext, useAccountContext} from '../../../contexts/AccountContext';
import {ListPots_pots} from '../../../schema-types/ListPots';

import {
  POTS_ANNUITY,
  POTS_BOND,
  POTS_GIA,
  POTS_ISA,
  POTS_JISA,
  POTS_JSIPP,
  POTS_PENSION,
  POTS_PROTECTION,
} from '../../../utils/potsFilter';
import {FieldValue, Option} from '../../../components/AutoForm/AutoFieldNew';
import withAutoFormNew, {AutoFormProps} from '../../../components/AutoForm/AutoFormNew';
import {BuilderPlanTypeContainer} from './BuilderSourceDestination/BuilderPlanTypeContainer';
import {CreateBuilderFooter} from './BuilderSourceDestination/CreateBuilderFooter';
import {builderFieldsType} from './builderFields';

import {ShowIfDebugWrapper} from '../../../contexts/DebugProvider';

import Loading from '../../../components/Loading/Loading';
import {useAutoForm} from '../../../components/AutoForm/useAutoForm';
import {AccountBasicFragment} from '../../../schema-types/AccountBasicFragment';
import {CreateBuilderHeader} from './CreateBuilderHeader';
import {CreateBuilderSPPDAHeader} from './CreateBuilderSPPDAHeader';
import {getEnvironment} from '../../../utils/envTest';
import {useIsMinor} from '../hooks/createBuilderHooks';

const COMMON_PLAN_TYPE: Option<
  typeof POTS_PENSION | typeof POTS_GIA | typeof POTS_BOND
>[] = [
  {
    value: POTS_PENSION,
    label: 'Pension',
  },
  {
    value: POTS_GIA,
    label: 'GIA',
    capitalise: true,
  },
  {
    value: POTS_BOND,
    label: 'Bond',
  },
];

export const MAIN_PLAN_TYPE: Option<
  typeof POTS_PENSION | typeof POTS_GIA | typeof POTS_BOND | typeof POTS_ISA
>[] = [
  ...COMMON_PLAN_TYPE,
  {
    value: POTS_ISA,
    label: 'ISA',
    capitalise: true,
  },
];

export const MAIN_PLAN_TYPE_MINOR: Option<typeof POTS_JSIPP | typeof POTS_JISA>[] = [
  {
    value: POTS_JISA,
    label: 'JISA',
    capitalise: true,
  },
  {
    value: POTS_JSIPP,
    label: 'Junior SIPP',
    capitalise: true,
  },
];

export const MAIN_PLAN_NEW: Option<typeof POTS_PROTECTION | typeof POTS_ANNUITY>[] = [
  {
    value: POTS_PROTECTION,
    label: 'Protection',
  },
  {
    value: POTS_ANNUITY,
    label: 'Annuity',
  },
];

// Depending on which plan action is selected, we need
// to be able to change the labelling of the plan types
type planLabelType = {
  first: string;
  second?: string;
};
export const HEADER_PLAN_LABELS: Record<OVRecommendActionEnum, planLabelType> = {
  [OVRecommendActionEnum.WITHDRAW]: {
    first: 'from a',
  },
  [OVRecommendActionEnum.CONTRIBUTE]: {
    first: 'to a',
  },
  [OVRecommendActionEnum.TRANSFER]: {
    first: 'to a',
    second: 'from a',
  },
  [OVRecommendActionEnum.SET_UP_NEW]: {
    first: '',
  },
  [OVRecommendActionEnum.MODIFY]: {
    first: 'an existing',
  },
  [OVRecommendActionEnum.ACTION]: {
    first: '',
  },
  [OVRecommendActionEnum.AD_HOC_FEE]: {
    first: '',
  },
};

export const filterPlanIfSPP =
  (values: builderFieldsType) =>
  (option: Option<string>): boolean => {
    if (values.isSppDa) {
      if (option.value === POTS_BOND) {
        return false;
      }
    }
    return true;
  };

export const getPlans = (
  potsData: ListPots_pots[],
  values: builderFieldsType,
  envName: string,
  isMinor: boolean
): {mainPlanTypes: Option<string>[]} => {
  if (isMinor) {
    return {
      mainPlanTypes: MAIN_PLAN_TYPE_MINOR,
    };
  }
  const {planAction} = values;
  const preMainPlanTypes = MAIN_PLAN_TYPE;

  let mainPlanTypes: Option<string>[] = [];

  if (planAction === OVRecommendActionEnum.SET_UP_NEW) {
    mainPlanTypes = [
      ...preMainPlanTypes,
      ...MAIN_PLAN_NEW.filter((p) => {
        return true;
      }),
    ];
    mainPlanTypes = mainPlanTypes.filter(filterPlanIfSPP(values));
    return {
      mainPlanTypes,
    };
  }

  if (
    !potsData ||
    // planAction === OVRecommendActionEnum.SET_UP_NEW ||
    planAction === OVRecommendActionEnum.TRANSFER
  ) {
    mainPlanTypes = [...preMainPlanTypes].filter(filterPlanIfSPP(values));
    return {
      mainPlanTypes,
    };
  }
  mainPlanTypes = preMainPlanTypes.filter(filterPlanIfSPP(values));
  return {
    mainPlanTypes,
  };
};

export const disableMainPlanType = (values: builderFieldsType) => {
  const {contact, planAction, mainPlanType} = values;
  switch (mainPlanType) {
    case POTS_ISA:
      return contact === OWNERSHIP.JOINT_CODE;
    case 'jisa':
      return contact === OWNERSHIP.JOINT_CODE;
    case POTS_PENSION:
      return contact === OWNERSHIP.JOINT_CODE;
    // case POTS_BOND:
    //   return contact === OWNERSHIP.JOINT_CODE;
    case POTS_PROTECTION:
      return (
        planAction === OVRecommendActionEnum.WITHDRAW ||
        planAction === OVRecommendActionEnum.CONTRIBUTE ||
        planAction === OVRecommendActionEnum.TRANSFER
      );
    default:
      return false;
  }
};

export const useBuilderPlanTypes = (values: builderFieldsType) => {
  const {pots: potsData, account} = useContext(AccountContext);
  const isMinor = useIsMinor();
  const envName = useMemo(() => {
    return getEnvironment();
  }, []);
  return useMemo(() => {
    const plans = getPlans(potsData, values, envName, isMinor);
    return {mainPlanTypes: uniqBy(plans.mainPlanTypes, 'value')};
  }, [values, potsData, isMinor]);
};

type BuilderProps = {
  builderAccount: AccountBasicFragment;
};

export const getMainPlanTypesFactory = ({
  values,
  mainPlanTypes,
}: {
  values: builderFieldsType;
  mainPlanTypes: Option<string>[];
}) => {
  return () => {
    if (values?.contact !== OWNERSHIP.JOINT_CODE) {
      return mainPlanTypes;
    }
    const mainPlans = mainPlanTypes.filter(
      (p) => p.value === POTS_GIA || p.value === POTS_BOND || p.value === POTS_PROTECTION
    );
    return mainPlans;
  };
};

const BuilderEditor: React.FC<AutoFormProps<builderFieldsType> & BuilderProps> = () => {
  const {values, errors, setFieldValue, handleSubmit, isSubmitting} =
    useAutoForm<builderFieldsType>();

  const {potsLoading} = useAccountContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const {accountType} = useAccountContext();
  const {builderDetails, isSPPDA: isSppDa} = useBuilderContext();

  useEffect(() => {
    if (builderDetails?.id || searchParams.get('ovrecommendId')) {
      return; // dont set search params if existing ovr
    }
    if (values.planAction) {
      searchParams.set('planAction', values.planAction);
    } else {
      searchParams.delete('planAction');
    }
    if (values.contact) {
      searchParams.set('contact', values.contact);
    } else {
      searchParams.delete('contact');
    }
    if (values.mainPlanType) {
      searchParams.set('mainPlanType', values.mainPlanType);
    } else {
      searchParams.delete('mainPlanType');
    }
    if (values.how) {
      searchParams.set('how', values.how);
    } else {
      searchParams.delete('how');
    }
    if (values.sppType) {
      searchParams.set('sppType', values.sppType);
    } else {
      searchParams.delete('sppType');
    }
    setSearchParams(searchParams, {replace: true});
  }, [
    values.planAction,
    values.contact,
    values.mainPlanType,
    builderDetails?.id,
    values.sppType,
    values.how,
  ]);

  return (
    <form onSubmit={handleSubmit}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Box
          sx={{
            minHeight: 'calc(100vh-100px)',
            // '& .MuiAccordionSummary-root.Mui-expanded': {
            //   minHeight: 44,
            // },
            // '& .MuiAccordionSummary-content.Mui-expanded': {
            //   margin: `12px 0`,
            // },
          }}
        >
          <ShowIfDebugWrapper debugKey="forms">
            isSppDa: {isSppDa ? 'true' : 'false'} | Partnership type: {accountType} |
            Recommend id: {builderDetails?.id || 'New recommend'}
          </ShowIfDebugWrapper>

          <Grid
            container
            direction="column"
            spacing={1}
            alignContent="stretch"
            justifyContent="space-around"
          >
            {isSppDa ? <CreateBuilderSPPDAHeader /> : <CreateBuilderHeader />}
            <Grid position="relative" item xs={12}>
              {disableMainPlanType(values) && (
                <Box my={1}>
                  <Grid container>
                    <Grid item>
                      <ErrorOutlineIcon color="primary" />
                    </Grid>
                    <Grid item style={{flexGrow: 1}}>
                      <Typography variant="body1">
                        You have selected a combination of options that cannot be used
                        together.
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
              {potsLoading ? (
                <Box mt={5}>
                  <Loading entityName="client accounts" />
                </Box>
              ) : (
                <BuilderPlanTypeContainer selectionError={disableMainPlanType(values)} />
              )}
            </Grid>
          </Grid>

          <CreateBuilderFooter />
        </Box>
      </LocalizationProvider>
    </form>
  );
};

export const BuilderEditorForm = withAutoFormNew<builderFieldsType, BuilderProps>(
  BuilderEditor
);

export type {CreateBuilderProps, createBuilderContext} from './useBuilderContext';
export {CreateBuilderContext} from './useBuilderContext';
